import React from "react";
import { useNavigate } from "react-router-dom";
import {
  GitBranch,
  Code,
  Clipboard,
  Play,
  Database,
  Copy,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../components/ui/card";
import { Button } from "../components/ui/button";

const FeatureCard = ({
  icon,
  title,
  description,
  linkTo,
  linkText,
  showButton = true,
}) => {
  const navigate = useNavigate();

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary">
            {icon}
          </div>
          <CardTitle>{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription className="text-sm">{description}</CardDescription>
      </CardContent>
      {showButton && (
        <CardFooter>
          <Button
            variant="outline"
            onClick={() => navigate(linkTo)}
            className="w-full"
          >
            {linkText}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

const DashboardPage = () => {
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold tracking-tight mb-2">
          Bitbucket Manager
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          A powerful tool for managing Bitbucket repository variables and
          pipeline operations across multiple repositories and workspaces.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        <FeatureCard
          icon={<Database className="h-5 w-5" />}
          title="Repository Variables"
          description="Manage repository variables across your Bitbucket repositories. Create, edit, and delete variables with ease."
          linkTo="/pipelines"
          linkText="Manage Variables"
        />

        <FeatureCard
          icon={<Copy className="h-5 w-5" />}
          title="Bulk Copy Variables"
          description="Copy variables from one repository to multiple others in a single operation, saving time when setting up new projects."
          linkTo="/pipelines"
          linkText="Copy Variables"
        />

        <FeatureCard
          icon={<Play className="h-5 w-5" />}
          title="Bulk Run Pipelines"
          description="Trigger pipeline runs across multiple repositories at once. Filter by project and select specific branches and pipeline types."
          linkTo="/pipelines"
          linkText="Run Pipelines"
        />

        <FeatureCard
          icon={<GitBranch className="h-5 w-5" />}
          title="Pipeline Status"
          description="View the status of your pipeline runs across repositories. Monitor build progress and identify issues quickly."
          linkTo="/pipelines-status"
          linkText="View Pipeline Status"
        />

        <FeatureCard
          icon={<Clipboard className="h-5 w-5" />}
          title="Project Organization"
          description="Group and organize repositories by projects for easier management. Filter repositories by name or project."
          linkTo="/pipelines"
          linkText="View Projects"
        />

        <FeatureCard
          icon={<Code className="h-5 w-5" />}
          title="Branch Management"
          description="Set default branches for your workflow and quickly search through available branches with the built-in autocomplete feature."
          linkTo="/pipelines"
          linkText="Manage Branches"
        />
      </div>

      <div className="bg-muted p-6 rounded-lg mb-8">
        <h2 className="text-xl font-semibold mb-3">Getting Started</h2>
        <ol className="list-decimal list-inside space-y-2 ml-2">
          <li>Select a workspace from the dropdown in any page</li>
          <li>
            Choose repositories to work with (filter by name or group by
            project)
          </li>
          <li>Set your default branch using the branch icon in the header</li>
          <li>Use the navigation links to access different features</li>
        </ol>
      </div>

      <div className="text-center text-sm text-muted-foreground">
        <p>
          This application interacts with the Bitbucket API to manage repository
          variables and pipelines. Make sure you have appropriate access
          permissions for the repositories you want to manage.
        </p>
      </div>
    </div>
  );
};

export default DashboardPage;
